/**
 * useFamilyForErasure — supplies the FamilySummary the confirm modal
 * needs (plan label + kid usernames). Both DeleteAccountEntry callers
 * use this so the modal looks the same from Manage Subscription or
 * Parental Controls.
 *
 * Plan label comes from useBilling (server-authoritative); kid names
 * come from /family/profiles, cached at module scope so the lookup
 * happens once per session.
 *
 * ``enabled`` holds the lookup until the surface is showing: Manage
 * Subscription is mounted closed on every signed-in boot (BillingChrome), kids
 * included, and fetching the parent-only roster there was a 403 on every
 * town boot (found by the Lands walks, 2026-10-25).
 */

import { useEffect, useState } from "react";
import { request } from "../api";
import { useBilling } from "../billing/useBilling";
import { WBM_PLANS } from "../billing/plans";
import type { FamilySummary } from "./erasureCategories";

interface ChildProfileItem {
  id: string;
  username: string;
}

let _cachedNames: string[] | null = null;
let _inflight: Promise<string[]> | null = null;

async function loadKidNames(): Promise<string[]> {
  if (_cachedNames) return _cachedNames;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const r = await request<{ children: ChildProfileItem[] }>("/family/profiles");
      _cachedNames = (r.children ?? []).map((c) => c.username);
    } catch {
      // No /family/profiles available (tests, signed-out, network) — render
      // the modal with a generic kid line. The category copy degrades to
      // "Your child profiles" without names.
      _cachedNames = [];
    } finally {
      _inflight = null;
    }
    return _cachedNames ?? [];
  })();
  return _inflight;
}

export function useFamilyForErasure(enabled = true): FamilySummary {
  const b = useBilling();
  const [kidNames, setKidNames] = useState<string[]>(_cachedNames ?? []);

  useEffect(() => {
    if (enabled && _cachedNames === null) void loadKidNames().then(setKidNames);
  }, [enabled]);

  const planLabel = b.plan_id
    ? (WBM_PLANS.find((p) => p.id === b.plan_id)?.name ?? null)
    : null;

  return { plan: planLabel, kidNames };
}
